import { Injectable, inject } from '@angular/core';
import { SessionStorage, SessionStorageService } from '@mwe/utils';
import { Address } from '@mwe/models';

const customerIdKey = 'caInformationRequest_customerId';
const accountIdKey = 'caInformationRequest_accountId';
const categoriesKey = 'caInformationRequest_categories';
const emailKey = 'caInformationRequest_email';
const updateEmailKey = 'caInformationRequest_updateEmail';
const addressKey = 'caInformationRequest_address';
const kindOfInformationKey = 'caInformationRequest_kindOfInformation';
const timeRangeTypeKey = 'caInformationRequest_timeRangeType';
const timeRangeFromKey = 'caInformationRequest_timeRangeFrom';
const timeRangeToKey = 'caInformationRequest_timeRangeTo';
const lastOrderIdKey = 'caInformationRequest_lastOrderId';
const retriedOnErrorKey = 'caInformationRequest_retriedOnError';
const submitIdKey = 'caInformationRequest_submitId';
const directAddressSelectionKey = 'caInformationRequest_directAddressSelection';

@Injectable({
  providedIn: 'root',
})
export class CAInformationRequestStateService {
  private storage = inject(SessionStorageService);

  @SessionStorage(directAddressSelectionKey)
  private _directAddressSelection: boolean;

  get directAddressSelection(): boolean {
    return !!this._directAddressSelection;
  }

  set directAddressSelection(value: boolean) {
    if (!value) {
      this.storage.clear(directAddressSelectionKey);
      return;
    }

    this._directAddressSelection = value;
  }

  @SessionStorage(customerIdKey)
  private _customerId: string;

  get customerId(): string {
    return this._customerId || undefined;
  }

  set customerId(customerId: string) {
    if (customerId) {
      this._customerId = customerId;
    } else {
      this.storage.clear(customerIdKey);
    }
  }

  @SessionStorage(accountIdKey)
  private _accountId: string;

  get accountId(): string {
    return this._accountId || undefined;
  }

  set accountId(accountId: string) {
    if (accountId) {
      this._accountId = accountId;
    } else {
      this.storage.clear(accountIdKey);
    }
  }

  @SessionStorage(emailKey)
  private _email: string;

  get email(): string {
    return this._email || undefined;
  }

  set email(email: string) {
    if (email) {
      this._email = email;
    } else {
      this.storage.clear(emailKey);
    }
  }

  @SessionStorage(updateEmailKey)
  private _updateEmail: boolean;

  get updateEmail(): boolean {
    return this._updateEmail || undefined;
  }

  set updateEmail(updateEmail: boolean) {
    if (updateEmail) {
      this._updateEmail = updateEmail;
    } else {
      this.storage.clear(updateEmailKey);
    }
  }

  @SessionStorage(kindOfInformationKey)
  private _kindOfInformation: string;

  get kindOfInformation(): string {
    return this._kindOfInformation || undefined;
  }

  set kindOfInformation(kindOfInformation: string) {
    if (kindOfInformation) {
      this._kindOfInformation = kindOfInformation;
    } else {
      this.storage.clear(kindOfInformationKey);
    }
  }

  @SessionStorage(timeRangeTypeKey)
  private _timeRangeType: string;

  get timeRangeType(): string {
    return this._timeRangeType || undefined;
  }

  set timeRangeType(timeRangeType: string) {
    if (timeRangeType) {
      this._timeRangeType = timeRangeType;
    } else {
      this.storage.clear(timeRangeTypeKey);
    }
  }

  @SessionStorage(timeRangeFromKey)
  private _timeRangeFrom: string;

  get timeRangeFrom(): string {
    return this._timeRangeFrom || undefined;
  }

  set timeRangeFrom(timeRangeFrom: string) {
    if (timeRangeFrom) {
      this._timeRangeFrom = timeRangeFrom;
    } else {
      this.storage.clear(timeRangeFromKey);
    }
  }

  @SessionStorage(timeRangeToKey)
  private _timeRangeTo: string;

  get timeRangeTo(): string {
    return this._timeRangeTo || undefined;
  }

  set timeRangeTo(timeRangeTo: string) {
    if (timeRangeTo) {
      this._timeRangeTo = timeRangeTo;
    } else {
      this.storage.clear(timeRangeToKey);
    }
  }

  @SessionStorage(addressKey)
  private _address: Address;

  get address(): Address {
    return this._address || undefined;
  }

  set address(address: Address) {
    if (address) {
      this._address = address;
    } else {
      this.storage.clear(addressKey);
    }
  }

  @SessionStorage(categoriesKey)
  private _categories: string[];

  get categories(): string[] {
    return this._categories || undefined;
  }

  set categories(categories: string[]) {
    if (categories) {
      this._categories = categories;
    } else {
      this.storage.clear(categoriesKey);
    }
  }

  @SessionStorage(lastOrderIdKey)
  private _lastOrderId: string;

  get lastOrderId(): string {
    return this._lastOrderId || undefined;
  }

  set lastOrderId(lastOrderId: string) {
    if (lastOrderId) {
      this._lastOrderId = lastOrderId;
    } else {
      this.storage.clear(lastOrderIdKey);
    }
  }

  @SessionStorage(retriedOnErrorKey)
  private _retriedOnError: boolean;

  get retriedOnError(): boolean {
    return this._retriedOnError || undefined;
  }

  set retriedOnError(retriedOnError: boolean) {
    if (retriedOnError) {
      this._retriedOnError = retriedOnError;
    } else {
      this.storage.clear(retriedOnErrorKey);
    }
  }

  @SessionStorage(submitIdKey)
  private _submitId: number;

  get submitId(): number {
    return this._submitId || undefined;
  }

  set submitId(submitId: number) {
    if (submitId) {
      this._submitId = submitId;
    } else {
      this.storage.clear(submitIdKey);
    }
  }

  clear(): void {
    this.storage.clear(directAddressSelectionKey);
    this.storage.clear(customerIdKey);
    this.storage.clear(accountIdKey);
    this.storage.clear(emailKey);
    this.storage.clear(updateEmailKey);
    this.storage.clear(kindOfInformationKey);
    this.storage.clear(timeRangeTypeKey);
    this.storage.clear(timeRangeFromKey);
    this.storage.clear(timeRangeToKey);
    this.storage.clear(categoriesKey);
    this.storage.clear(lastOrderIdKey);
    this.storage.clear(retriedOnErrorKey);
    this.storage.clear(submitIdKey);
  }
}
